const SHIPS = [
  { name: "Viper", color: "#00F0FF", shape: [[0,-18],[10,14],[0,7],[-10,14]], fireRate: 200, bSpd: 500 },
  { name: "Interceptor", color: "#FF003C", shape: [[0,-22],[6,4],[12,16],[0,10],[-12,16],[-6,4]], fireRate: 160, bSpd: 550 },
  { name: "Destroyer", color: "#8A2BE2", shape: [[0,-26],[7,-12],[16,14],[0,7],[-16,14],[-7,-12]], fireRate: 240, bSpd: 450 },
  { name: "Phantom", color: "#FFD700", shape: [[0,-28],[5,-16],[14,-6],[16,10],[7,16],[0,12],[-7,16],[-16,10],[-14,-6],[-5,-16]], fireRate: 130, bSpd: 600 },
];

const AST_COLORS = [
  { color: "#888888", mult: 1 },
  { color: "#33FF66", mult: 1.5 },
  { color: "#3399FF", mult: 2 },
  { color: "#AA33FF", mult: 3 },
  { color: "#FFD700", mult: 5 },
];

export default class AsteroidsEngine {
  constructor(canvas, opts) {
    this.cvs = canvas;
    this.ctx = canvas.getContext("2d");
    this.W = canvas.width;
    this.H = canvas.height;
    this.def = { ...SHIPS[opts.shipType || 0] };
    this.loadout = opts.loadout || {};
    if (this.loadout.ship_color) this.def.color = this.loadout.ship_color;
    this.onScore = opts.onScore || (() => {});
    this.onLives = opts.onLives || (() => {});
    this.onGameOver = opts.onGameOver || (() => {});
    this.player = { x: this.W / 2, y: this.H / 2, rot: -Math.PI / 2, dx: 0, dy: 0, lives: 3, inv: 0, lastShot: 0 };
    this.asteroids = [];
    this.bullets = [];
    this.enemies = [];
    this.eBullets = [];
    this.particles = [];
    this.stars = Array.from({ length: 180 }, () => ({
      x: Math.random() * this.W, y: Math.random() * this.H,
      s: Math.random() * 1.4 + 0.3, a: Math.random() * 0.35 + 0.1,
    }));
    this.score = 0;
    this.lvl = 1;
    this.over = false;
    this.elapsed = 0;
    this.spCd = 3;
    this.eCd = 15;
    this.keys = {};
    this.raf = null;
    this.prev = 0;
    this._wave(4);
  }

  start() {
    this._kd = (e) => { this.keys[e.code] = true; if (["Space","ArrowUp","ArrowDown","ArrowLeft","ArrowRight"].includes(e.code)) e.preventDefault(); };
    this._ku = (e) => { this.keys[e.code] = false; };
    window.addEventListener("keydown", this._kd);
    window.addEventListener("keyup", this._ku);
    this.prev = performance.now();
    this.raf = requestAnimationFrame((t) => this._loop(t));
  }

  stop() {
    if (this.raf) cancelAnimationFrame(this.raf);
    if (this._kd) window.removeEventListener("keydown", this._kd);
    if (this._ku) window.removeEventListener("keyup", this._ku);
  }

  forceEnd() { this.over = true; this.onGameOver(this.score); }

  _loop(ts) {
    const dt = Math.min((ts - this.prev) / 1000, 0.05);
    this.prev = ts;
    if (!this.over) { this.elapsed += dt; this.lvl = 1 + Math.floor(this.elapsed / 15); this._upd(dt, ts); }
    this._draw();
    if (!this.over) this.raf = requestAnimationFrame((t) => this._loop(t));
  }

  _upd(dt, ts) {
    const p = this.player;
    if (this.keys.ArrowLeft || this.keys.KeyA) p.rot -= 4.5 * dt;
    if (this.keys.ArrowRight || this.keys.KeyD) p.rot += 4.5 * dt;
    if (this.keys.ArrowUp || this.keys.KeyW) {
      p.dx += Math.cos(p.rot) * 280 * dt;
      p.dy += Math.sin(p.rot) * 280 * dt;
      let tc = this.def.color;
      if (this.loadout.trail === "flame") tc = "#FF6600";
      else if (this.loadout.trail === "electric") tc = "#88CCFF";
      else if (this.loadout.trail === "rainbow") { const cs = ["#FF3344","#33FF66","#3399FF","#AA33FF","#FFD700"]; tc = cs[Math.floor(ts / 80) % cs.length]; }
      if (Math.random() > 0.4) this.particles.push({ x: p.x-Math.cos(p.rot)*14, y: p.y-Math.sin(p.rot)*14, dx: -Math.cos(p.rot)*80+(Math.random()-0.5)*50, dy: -Math.sin(p.rot)*80+(Math.random()-0.5)*50, life: 0.3, ml: 0.3, c: tc, sz: 2.5 });
    }
    const sp = Math.hypot(p.dx, p.dy);
    if (sp > 380) { p.dx *= 380/sp; p.dy *= 380/sp; }
    p.dx *= 0.985; p.dy *= 0.985;
    p.x = ((p.x + p.dx*dt)%this.W + this.W)%this.W;
    p.y = ((p.y + p.dy*dt)%this.H + this.H)%this.H;
    if (p.inv > 0) p.inv -= dt;

    // Shoot
    if (this.keys.Space && ts - p.lastShot > this.def.fireRate) {
      p.lastShot = ts;
      const lt = this.loadout.laser;
      const bSz = lt === "wide" ? 4 : 2;
      const mkBullet = (offX, offY, color) => ({
        x: p.x + Math.cos(p.rot)*20 + offX, y: p.y + Math.sin(p.rot)*20 + offY,
        dx: Math.cos(p.rot)*this.def.bSpd + p.dx*0.2, dy: Math.sin(p.rot)*this.def.bSpd + p.dy*0.2,
        life: 1.2, sz: bSz, c: color,
      });
      if (lt === "dual") {
        const perp = p.rot + Math.PI/2;
        this.bullets.push(mkBullet(Math.cos(perp)*5, Math.sin(perp)*5, this.def.color));
        this.bullets.push(mkBullet(-Math.cos(perp)*5, -Math.sin(perp)*5, this.def.color));
      } else {
        let bc = this.def.color;
        if (lt === "rainbow") { const cs = ["#FF3344","#33FF66","#3399FF","#AA33FF","#FFD700","#FF33AA"]; bc = cs[Math.floor(ts / 100) % cs.length]; }
        this.bullets.push(mkBullet(0, 0, bc));
      }
    }

    this.bullets = this.bullets.filter(b => { b.x=((b.x+b.dx*dt)%this.W+this.W)%this.W; b.y=((b.y+b.dy*dt)%this.H+this.H)%this.H; b.life-=dt; return b.life>0; });
    for (const a of this.asteroids) { a.x=((a.x+a.dx*dt)%this.W+this.W)%this.W; a.y=((a.y+a.dy*dt)%this.H+this.H)%this.H; a.rot+=a.rs*dt; }
    for (const e of this.enemies) {
      const ang = Math.atan2(p.y-e.y, p.x-e.x);
      e.dx += Math.cos(ang)*55*dt; e.dy += Math.sin(ang)*55*dt;
      const es = Math.hypot(e.dx, e.dy); if (es > 140) { e.dx*=140/es; e.dy*=140/es; }
      e.x=((e.x+e.dx*dt)%this.W+this.W)%this.W; e.y=((e.y+e.dy*dt)%this.H+this.H)%this.H;
      e.rot = ang; e.fc -= dt;
      if (e.fc <= 0) { e.fc = Math.max(1.8-this.lvl*0.12, 0.5); this.eBullets.push({ x: e.x+Math.cos(ang)*14, y: e.y+Math.sin(ang)*14, dx: Math.cos(ang)*220, dy: Math.sin(ang)*220, life: 2.5 }); }
    }
    this.eBullets = this.eBullets.filter(b => { b.x+=b.dx*dt; b.y+=b.dy*dt; b.life-=dt; return b.life>0; });
    this.particles = this.particles.filter(pt => { pt.x+=pt.dx*dt; pt.y+=pt.dy*dt; pt.life-=dt; return pt.life>0; });
    this.spCd -= dt;
    if (this.spCd <= 0) { this.spCd = Math.max(5-this.lvl*0.3, 1.5); this._wave(Math.min(1+Math.floor(this.lvl/2),5)); }
    if (this.elapsed > 20) { this.eCd -= dt; if (this.eCd <= 0) { this.eCd = Math.max(12-this.lvl, 3); this._spawnE(); } }
    this._coll();
  }

  _coll() {
    const p = this.player;
    for (let bi = this.bullets.length-1; bi >= 0; bi--) {
      let hit = false;
      for (let ai = this.asteroids.length-1; ai >= 0; ai--) {
        if (this._d(this.bullets[bi], this.asteroids[ai]) < this.asteroids[ai].r + (this.bullets[bi].sz || 2)) {
          this.bullets.splice(bi, 1); this._breakA(ai); hit = true; break;
        }
      }
      if (hit) continue;
      if (bi >= this.bullets.length) continue;
      for (let ei = this.enemies.length-1; ei >= 0; ei--) {
        if (this._d(this.bullets[bi], this.enemies[ei]) < 14) {
          this._boom(this.enemies[ei].x, this.enemies[ei].y, "#FF003C", 12);
          this.enemies.splice(ei, 1); this.bullets.splice(bi, 1);
          this.score += 150; this.onScore(this.score); break;
        }
      }
    }
    if (p.inv > 0) return;
    for (let ai = this.asteroids.length-1; ai >= 0; ai--) {
      if (this._d(p, this.asteroids[ai]) < 12+this.asteroids[ai].r) { this._breakA(ai); this._die(); return; }
    }
    for (let ei = this.enemies.length-1; ei >= 0; ei--) {
      if (this._d(p, this.enemies[ei]) < 24) { this._boom(this.enemies[ei].x, this.enemies[ei].y, "#FF003C", 12); this.enemies.splice(ei, 1); this._die(); return; }
    }
    for (let bi = this.eBullets.length-1; bi >= 0; bi--) {
      if (this._d(p, this.eBullets[bi]) < 12) { this.eBullets.splice(bi, 1); this._die(); return; }
    }
  }

  _die() { this._boom(this.player.x, this.player.y, this.def.color, 18); this.player.lives--; this.player.inv = 2.5; this.onLives(this.player.lives); if (this.player.lives <= 0) { this.over = true; this.onGameOver(this.score); } }

  _breakA(i) {
    const a = this.asteroids.splice(i,1)[0];
    const ac = AST_COLORS[a.ci || 0];
    this._boom(a.x, a.y, ac.color, 6);
    if (a.sz==="lg") this.score += Math.round(25 * ac.mult);
    else if (a.sz==="md") this.score += Math.round(50 * ac.mult);
    else this.score += Math.round(100 * ac.mult);
    this.onScore(this.score);
    if (a.sz === "lg") { for (let j=0;j<2+Math.floor(Math.random()*2);j++) { const ch = this._mkAst(a.x,a.y,"md"); ch.ci = a.ci; this.asteroids.push(ch); } }
    else if (a.sz === "md") { for (let j=0;j<2;j++) { const ch = this._mkAst(a.x,a.y,"sm"); ch.ci = a.ci; this.asteroids.push(ch); } }
  }

  _d(a, b) { return Math.hypot(a.x-b.x, a.y-b.y); }
  _wave(n) { for (let i=0; i<n; i++) { let x,y; do { x=Math.random()*this.W; y=Math.random()*this.H; } while(this._d({x,y},this.player)<120); this.asteroids.push(this._mkAst(x,y,"lg")); } }

  _pickColor() {
    const r = Math.random(), l = this.lvl;
    if (l >= 5 && r < 0.05) return 4;
    if (l >= 3 && r < 0.15) return 3;
    if (l >= 2 && r < 0.30) return 2;
    if (r < 0.45) return 1;
    return 0;
  }

  _mkAst(x, y, sz) {
    const R = {lg:32,md:16,sm:8}; const S = {lg:35,md:60,sm:95};
    const r = R[sz]; const base = S[sz]+this.lvl*6;
    const ang = Math.random()*Math.PI*2;
    const nP = sz==="lg"?10:sz==="md"?7:5;
    const pts = [];
    for (let i=0; i<nP; i++) { const a=(i/nP)*Math.PI*2; const rr=r*(0.7+Math.random()*0.3); pts.push([Math.cos(a)*rr, Math.sin(a)*rr]); }
    return { x, y, dx: Math.cos(ang)*base*(0.6+Math.random()*0.8), dy: Math.sin(ang)*base*(0.6+Math.random()*0.8), r, sz, rot: 0, rs: (Math.random()-0.5)*2, pts, ci: this._pickColor() };
  }

  _spawnE() {
    const s = Math.floor(Math.random()*4);
    const x = s<2?(s===0?-10:this.W+10):Math.random()*this.W;
    const y = s>=2?(s===2?-10:this.H+10):Math.random()*this.H;
    this.enemies.push({ x, y, dx: 0, dy: 0, rot: 0, fc: 2 });
  }

  _boom(x, y, baseColor, n) {
    const et = this.loadout.explosion;
    for (let i=0;i<n;i++) {
      const a=Math.random()*Math.PI*2; const s=40+Math.random()*130;
      let color = baseColor;
      if (et === "rainbow") { const cs=["#FF3344","#33FF66","#3399FF","#AA33FF","#FFD700","#FF33AA"]; color=cs[Math.floor(Math.random()*cs.length)]; }
      else if (et === "firework") { color = Math.random()>0.5?"#FFD700":"#FF6600"; }
      else if (et === "plasma") { color = Math.random()>0.5?"#33FF66":"#3399FF"; }
      this.particles.push({x,y,dx:Math.cos(a)*s,dy:Math.sin(a)*s,life:0.25+Math.random()*0.35,ml:0.6,c:color,sz:et==="firework"?1+Math.random()*4:1+Math.random()*2.5});
    }
  }

  _drawDecal(x, y, rot) {
    const d = this.loadout.decal;
    if (!d) return;
    const c = this.ctx;
    c.save(); c.translate(x, y); c.rotate(rot + Math.PI/2);
    const s = 5;
    if (d === "star") {
      c.fillStyle = "rgba(255,255,255,0.5)"; c.beginPath();
      for (let i=0;i<5;i++){const a=(i*4*Math.PI/5)-Math.PI/2;if(i===0)c.moveTo(Math.cos(a)*s,Math.sin(a)*s);else c.lineTo(Math.cos(a)*s,Math.sin(a)*s);}
      c.closePath(); c.fill();
    } else if (d === "lightning") {
      c.strokeStyle = "#FFD700"; c.lineWidth = 1.5; c.beginPath();
      c.moveTo(-2,-s); c.lineTo(1,-1); c.lineTo(-1,1); c.lineTo(2,s); c.stroke();
    } else if (d === "flame") {
      c.fillStyle = "#FF6600"; c.beginPath();
      c.moveTo(0,-s); c.quadraticCurveTo(s,-s/2,0,s); c.quadraticCurveTo(-s,-s/2,0,-s); c.fill();
    } else if (d === "skull") {
      c.fillStyle = "rgba(255,255,255,0.4)"; c.beginPath(); c.arc(0,-1,s*0.6,0,Math.PI*2); c.fill();
      c.fillStyle = "#050508"; c.beginPath(); c.arc(-2,-2,1.5,0,Math.PI*2); c.fill(); c.beginPath(); c.arc(2,-2,1.5,0,Math.PI*2); c.fill();
    } else if (d === "diamond") {
      c.fillStyle = "#3399FF"; c.beginPath();
      c.moveTo(0,-s); c.lineTo(s*0.6,0); c.lineTo(0,s); c.lineTo(-s*0.6,0); c.closePath(); c.fill();
    }
    c.restore();
  }

  _draw() {
    const c = this.ctx;
    c.fillStyle = "#050508"; c.fillRect(0,0,this.W,this.H);
    for (const s of this.stars) { c.fillStyle=`rgba(255,255,255,${s.a})`; c.beginPath(); c.arc(s.x,s.y,s.s,0,6.28); c.fill(); }
    for (const pt of this.particles) { c.globalAlpha=pt.life/pt.ml; c.fillStyle=pt.c; c.beginPath(); c.arc(pt.x,pt.y,pt.sz,0,6.28); c.fill(); }
    c.globalAlpha = 1;
    // Asteroids with colors
    for (const a of this.asteroids) {
      const ac = AST_COLORS[a.ci || 0];
      c.save(); c.translate(a.x,a.y); c.rotate(a.rot);
      c.strokeStyle = ac.color; c.lineWidth = 1.5;
      c.fillStyle = ac.color + "15";
      c.beginPath(); a.pts.forEach(([px,py],i)=>i===0?c.moveTo(px,py):c.lineTo(px,py)); c.closePath(); c.fill(); c.stroke();
      // Glow for rare asteroids
      if (a.ci >= 3) { c.shadowColor = ac.color; c.shadowBlur = 8; c.stroke(); c.shadowBlur = 0; }
      c.restore();
    }
    // Enemies
    for (const e of this.enemies) { c.save(); c.translate(e.x,e.y); c.rotate(e.rot); c.fillStyle="#FF003C"; c.shadowColor="#FF003C"; c.shadowBlur=8; c.beginPath(); c.moveTo(14,0); c.lineTo(-9,7); c.lineTo(-5,0); c.lineTo(-9,-7); c.closePath(); c.fill(); c.shadowBlur=0; c.restore(); }
    // Enemy bullets
    c.fillStyle="#FF003C"; for (const b of this.eBullets) { c.beginPath(); c.arc(b.x,b.y,2.5,0,6.28); c.fill(); }
    // Player bullets with custom colors/sizes
    for (const b of this.bullets) { c.fillStyle=b.c||this.def.color; c.shadowColor=b.c||this.def.color; c.shadowBlur=5; c.beginPath(); c.arc(b.x,b.y,b.sz||2,0,6.28); c.fill(); } c.shadowBlur=0;
    // Player ship
    if (!this.over) { const p=this.player; if(p.inv<=0||Math.floor(p.inv*8)%2===0){c.save();c.translate(p.x,p.y);c.rotate(p.rot+Math.PI/2);c.fillStyle=this.def.color;c.strokeStyle=this.def.color;c.lineWidth=1.5;c.shadowColor=this.def.color;c.shadowBlur=10;c.beginPath();this.def.shape.forEach(([sx,sy],i)=>i===0?c.moveTo(sx,sy):c.lineTo(sx,sy));c.closePath();c.fill();c.stroke();c.shadowBlur=0;c.restore();this._drawDecal(p.x,p.y,p.rot);} }
  }
}
